import { useAuth } from "../../context/AuthContext";
import { useMemo, useState } from "react";
import { Download, ReceiptText } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Dropdown } from "../../components/ui/dropdown";
import {
  DateRangePicker, presetRange, type DateRangeValue,
} from "../../components/ui/date-range-picker";
import { useToast } from "../../components/ui/toast";
import { formatDuration, formatTimestamp } from "../../lib/time";
import { formatINR, cn } from "../../lib/utils";
import { USAGE_EVENT_META, USAGE_TYPE_OPTIONS, type UsageEvent, type UsageEventType } from "./types";

const TH = "px-4 py-3 text-left font-mono text-xs font-semibold uppercase tracking-wider";
const TD = "px-4 py-3.5 align-middle";

export function UsageLogsTab({ events }: { events: UsageEvent[] }) {
  const { user } = useAuth() as any;
  const toast = useToast();
  const [range, setRange] = useState<DateRangeValue>(() => ({ ...presetRange("last_30"), preset: "last_30" }));
  const [type, setType] = useState<UsageEventType | "all">("all");

  const filtered = useMemo(() => events.filter((e) => {
    const t = new Date(e.timestamp);
    if (t < range.start || t > new Date(range.end.getTime() + 864e5 - 1)) return false;
    if (type !== "all" && e.type !== type) return false;
    return true;
  }), [events, range, type]);

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 rounded-xl shadow-card dark:shadow-card-dark bg-paper-card dark:bg-[#292524] p-3 sm:p-3.5">
        <DateRangePicker value={range} onChange={setRange} align="right" />
        <Dropdown
          ariaLabel="Filter by event type"
          className="w-44"
          value={type}
          options={USAGE_TYPE_OPTIONS}
          onChange={setType}
        />
        <div className="flex-1" />
        <button
          onClick={() => toast.info("Exporting usage logs…", "CSV will be emailed when ready.")}
          className="flex items-center gap-2 rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink transition-colors hover:bg-soft"
        >
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl shadow-card dark:shadow-card-dark bg-paper-card dark:bg-[#292524]">
        <table className="w-full text-sm">
          <thead className="bg-soft text-ink-3">
            <tr>
              <th className={TH}>Timestamp</th>
              <th className={TH}>Event Type</th>
              <th className={TH}>Campaign / Batch</th>
              <th className={TH}>Duration</th>
              <th className={cn(TH, "text-right")}>Cost</th>
              <th className={cn(TH, "text-right")}>Balance After</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e) => {
              const meta = USAGE_EVENT_META[e.type];
              return (
                <tr key={e.id} className="border-t border-line transition-colors hover:bg-soft/50">
                  <td className={cn(TD, "font-mono text-xs text-ink-2")}>{formatTimestamp(e.timestamp, user?.timezone)}</td>
                  <td className={TD}><Badge tone={meta.tone}>{meta.label}</Badge></td>
                  <td className={cn(TD, "text-ink")}>{e.campaign ?? <span className="text-ink-3">—</span>}</td>
                  <td className={cn(TD, "font-mono text-ink-2")}>
                    {e.durationSec !== null ? formatDuration(e.durationSec) : "—"}
                  </td>
                  <td className={cn(TD, "text-right font-mono font-semibold", e.amount < 0 ? "text-danger" : "text-success")}>
                    {e.amount < 0 ? "−" : "+"} {formatINR(Math.abs(e.amount))}
                  </td>
                  <td className={cn(TD, "text-right font-mono text-ink")}>{formatINR(e.balanceAfter)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {filtered.length === 0 && (
          <div className="p-12 text-center">
            <ReceiptText className="mx-auto h-10 w-10 text-line" />
            <p className="mt-3 text-sm text-ink-2">No usage events in this window.</p>
          </div>
        )}
      </div>
    </div>
  );
}
